import { useEffect, useId, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, Info, ShieldCheck } from 'lucide-react'
import { useFocusTrap } from '../hooks/useFocusTrap'

export interface ConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void | Promise<void>
  title: string
  message: ReactNode
  confirmText?: string
  cancelText?: string
  type?: 'danger' | 'warning' | 'info'
  /** the confirm button waits */
  busy?: boolean
}

const STYLE = {
  danger: {
    bg: 'bg-red-100 dark:bg-red-900/30',
    icon: 'text-red-600 dark:text-red-400',
    btn: 'btn-danger',
    Icon: AlertTriangle,
  },
  warning: {
    bg: 'bg-amber-100 dark:bg-amber-900/30',
    icon: 'text-amber-600 dark:text-amber-400',
    btn: 'btn-primary',
    Icon: ShieldCheck,
  },
  info: { bg: 'bg-primary-soft', icon: 'text-primary', btn: 'btn-primary', Icon: Info },
}

/** Every destructive or irreversible action asks first (delete a node, Make live, Roll back, Retire). */
export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'danger',
  busy = false,
}: ConfirmDialogProps) {
  const titleId = useId()
  const ref = useFocusTrap<HTMLDivElement>(isOpen)
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])
  if (!isOpen) return null
  const s = STYLE[type]
  return createPortal(
    <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        ref={ref}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-md border border-slate-200 bg-white p-5 shadow-2xl outline-none animate-pop dark:border-slate-700 dark:bg-surface-dark"
      >
        <div className="flex items-start gap-3">
          <div className={`${s.bg} shrink-0 rounded-full p-2.5`}>
            <s.Icon className={s.icon} size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 id={titleId} className="text-sm font-semibold text-default">
              {title}
            </h3>
            <div className="mt-1.5 text-xs text-muted">{message}</div>
          </div>
        </div>
        <div className="mt-5 flex gap-2">
          <button type="button" onClick={onClose} className="btn btn-secondary flex-1" disabled={busy}>
            {cancelText}
          </button>
          <button
            type="button"
            onClick={async () => {
              await onConfirm()
              onClose()
            }}
            className={`btn ${s.btn} flex-1`}
            disabled={busy}
          >
            {busy ? 'Working…' : confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
